<div class="app-container" [class.tooltips-disabled]="!hoverTooltipsEnabled" [class.labels-hidden]="!searchBoxLabelsVisible">
  <div class="main-content">
    <!-- Header -->
    <div class="header">
      <h1>🔧 OCR Debug Line Item Creator</h1>
      <p>Select line items and value search boxes for OCR data extraction</p>
    </div>

    <!-- Main Container -->
    <div class="main-container">
      <div id="content">
        <!-- Image Container -->
        @if (ocrData) {
          <div class="image-container"
            #imageContainer
            (mousedown)="onMouseDown($event)"
            (mousemove)="onMouseMove($event)"
            (mouseup)="onMouseUp($event)"
            (mouseleave)="onMouseLeave($event)">
            @if (currentImage) {
              <img
                [src]="currentImage"
                class="base-image"
                alt="OCR Page"
                (load)="onImageLoad($event)">
            }
            @if (!currentImage) {
              <div class="placeholder">
                📄 Upload page.png<br><small>for actual word alignment</small>
              </div>
            }
            <!-- OCR Boxes Overlay -->
            <div class="ocr-overlay">
              @for (box of ocrData.boxes | slice:0:boxLimit; track box; let i = $index) {
                <div
                  class="ocr-box"
                  [class.selected-words]="selectedBoxIndices.includes(i)"
                  [class.line-item-box]="lineItemBoxIndices.includes(i)"
                  [class.anchor-box]="isInAnyAnchor(i)"
                  [style.opacity]="boxOpacity"
                  [ngStyle]="getBoxStyle(box)">
                  <div class="ocr-text">
                    "{{box.raw_text}}"<br>
                    📊 {{box.confidence | number:'1.0-0'}}%<br>
                    📐 Orig: ({{box.ulc.x | number:'1.0-0'}}, {{box.ulc.y | number:'1.0-0'}})<br>
                    🔄 Trans: ({{transformCoordinates(box).x | number:'1.0-0'}}, {{transformCoordinates(box).y | number:'1.0-0'}})<br>
                    📏 {{transformCoordinates(box).width | number:'1.0-0'}}×{{transformCoordinates(box).height | number:'1.0-0'}}
                  </div>
                </div>
              }
            </div>
            <!-- Existing Line Items Search Boxes -->
            @if (existingLineItems.length > 0) {
              <div class="line-item-overlay">
                @for (item of existingLineItems; track item) {
                  @if (item.search_box) {
                    <div
                      class="existing-search-box"
                      [class.selected-for-deletion]="selectedLineItemForDeletion === item.id"
                      [attr.data-line-item-name]="item.id"
                      [ngStyle]="getSearchBoxStyle(item)"
                      (click)="onSearchBoxClick(item, $event)">
                      <div class="line-item-label" [attr.data-item-id]="item.id">
                        {{item.line_item_text || item.id}}
                      </div>
                    </div>
                  }
                }
              </div>
            }
            <!-- Current Selections Visual Overlay -->
            <div class="current-selections-visual-overlay">
              <!-- Line Item Text Preview -->
              @if (lineItemSelections.line_item_text) {
                <div
                  class="selection-box-preview line-item-preview"
                  [ngStyle]="getCurrentSelectionBoxStyle(lineItemSelections.line_item_text)">
                  <div class="selection-label">LINE ITEM TEXT</div>
                </div>
              }
              <!-- Anchors Preview -->
              @for (anchor of lineItemSelections.anchors; track anchor; let i = $index) {
                <div class="selection-box-preview anchor-preview"
                  [ngStyle]="getCurrentSelectionBoxStyle(anchor)">
                  <div class="selection-label">ANCHOR {{i + 1}}</div>
                </div>
              }
              <!-- Value Search Box Preview -->
              @if (lineItemSelections.value_search_box) {
                <div
                  class="selection-box-preview value-preview"
                  [ngStyle]="getCurrentSelectionBoxStyle(lineItemSelections.value_search_box)">
                  <div class="selection-label">VALUE SEARCH BOX</div>
                </div>
              }
            </div>
            <!-- Selection Overlay -->
            <div class="selection-overlay" [class.selection-mode-active]="isSelectionActive">
              @if (isDrawing && currentSelectionRect) {
                <div
                  class="selection-rectangle"
                  [ngStyle]="currentSelectionRect">
                </div>
              }
            </div>
          </div>
        }

        <!-- Empty State -->
        @if (!ocrData) {
          <div class="empty-state">
            <div class="icon">📁</div>
            <h3>No Data Loaded</h3>
            <p>Please select a year, document, and page from the sidebar to begin.</p>
          </div>
        }
      </div>

      <!-- Debug Info -->
      <div class="debug-info" style="display: none;">
        <div><strong>🔍 Debug Information:</strong></div>
        <div id="debugDetails">Click "Debug Info" to show coordinate details</div>
      </div>
    </div>
  </div>

  <!-- Sidebar -->
  <div class="sidebar">
    <!-- Data Selection -->
    <div class="sidebar-section">
      <h3>📁 Data Selection</h3>
      <div class="debug-grid">
        <div class="debug-group">
          <label>Tax Return Type:</label>
          <select [(ngModel)]="selectedTaxReturnType" (change)="onTaxReturnTypeChange()" class="tax-type-select">
            @for (type of taxReturnTypes; track type) {
              <option [value]="type.id">{{type.name}}</option>
            }
          </select>
        </div>

        <div class="debug-group">
          <label>Year:</label>
          <select [(ngModel)]="selectedYear" (change)="onYearChange()">
            <option value="">Select Year...</option>
            @for (year of years; track year) {
              <option [value]="year">{{year}}</option>
            }
          </select>
        </div>

        <div class="debug-group">
          <label>Document:</label>
          <select [(ngModel)]="selectedDocument" (change)="onDocumentChange()" [disabled]="!selectedYear">
            <option value="">Select Document...</option>
            @for (doc of documents; track doc) {
              <option [value]="doc.id">{{doc.name}}</option>
            }
          </select>
        </div>

        <div class="debug-group">
          <label>Language:</label>
          <select [(ngModel)]="selectedLanguage" (change)="onLanguageChange()" [disabled]="!selectedDocument">
            <option value="">Select Language...</option>
            @for (lang of languages; track lang) {
              <option [value]="lang.id">{{lang.name}}</option>
            }
          </select>
        </div>

        <div class="debug-group">
          <label>Page:</label>
          <select [(ngModel)]="selectedPage" (change)="onPageChange()" [disabled]="!selectedLanguage">
            <option [value]="null">Select Page...</option>
            @for (page of pages; track page) {
              <option [value]="page">Page {{page}}</option>
            }
          </select>
        </div>

        <!-- OCR Status Warning -->
        @if (selectedPage && !hasOcrData) {
          <div class="ocr-warning">
            <div class="warning-icon">⚠️</div>
            <div class="warning-text">
              <strong>No OCR Data Available</strong>
              <p>Generate OCR data first to continue</p>
            </div>
          </div>
        }

        <!-- Generate OCR Button (prominent when no OCR data) -->
        @if (selectedPage && !hasOcrData) {
          <button
            (click)="generateOCR()"
            class="btn-generate-prominent">
            🔄 Generate OCR Data
          </button>
        }

        <!-- Load Data Button (only enabled when OCR exists) -->
        <button (click)="loadSelectedData()"
          [disabled]="!selectedPage || !hasOcrData"
          [class.btn-disabled]="!hasOcrData">
          📂 Load Selected Data
        </button>
      </div>
    </div>

    <!-- Line Item Selection -->
    <div class="sidebar-section">
      <h3>✏️ Line Item Selection</h3>
      <div class="debug-grid">
        <div class="debug-group">
          <label>Selection Mode:</label>
          <select [(ngModel)]="currentSelectionMode" (change)="onSelectionModeChange()">
            <option value="">Select Mode...</option>
            <option value="line_item">Line Item</option>
            <option value="anchors">Anchors</option>
            <option value="value_search_box">Value Search Box</option>
          </select>
        </div>

        <div class="debug-group">
          <button (click)="toggleSelection()" [disabled]="!currentSelectionMode" class="btn-selection">
            {{isSelectionActive ? '⏹️ Disable Selection' : '🎯 Enable Selection'}}
          </button>
        </div>

        <div class="debug-group">
          <label>Line Item Name (optional):</label>
          <input type="text" [(ngModel)]="lineItemName" placeholder="e.g., line_15 (auto-generated if empty)" maxlength="50">
        </div>

        <div class="debug-group">
          <button (click)="saveLineItem()" [disabled]="!lineItemSelections.value_search_box" class="btn-save">
            💾 Save Line Item
          </button>
        </div>

        <div class="debug-group">
          <button (click)="clearSelections()" class="btn-clear">
            🗑️ Clear All Selections
          </button>
        </div>

        <div class="debug-group delete-section">
          <label>Delete Existing Line Item:</label>
          @if (!selectedLineItemForDeletion) {
            <div class="delete-info">
              <small>Click on any existing search box (orange dashed) to select it for deletion</small>
            </div>
          }
          @if (selectedLineItemForDeletion) {
            <div class="delete-selected">
              <span class="selected-item-name">📦 {{selectedLineItemForDeletion}}</span>
              <button (click)="deleteSelectedLineItem()" class="btn-delete">
                🗑️ Delete Selected
              </button>
              <button (click)="clearDeleteSelection()" class="btn-cancel-delete">
                ✖️ Cancel
              </button>
            </div>
          }
        </div>
      </div>

      <!-- Selection Preview -->
      @if (lineItemSelections.line_item_text || lineItemSelections.anchors.length > 0 || lineItemSelections.value_search_box) {
        <div class="selection-preview">
          <h4>📋 Current Selections:</h4>
          @if (lineItemSelections.line_item_text) {
            <div class="preview-item">
              <span class="preview-label line-item-label-preview">📝 Line Item Text</span>
              <span class="preview-text">{{lineItemText}}</span>
            </div>
          }
          @for (anchor of lineItemSelections.anchors; track anchor; let i = $index) {
            <div class="preview-item">
              <span class="preview-label anchor-label-preview">🎯 Anchor {{i + 1}}</span>
              <span class="preview-text">{{anchorTexts[i]}}</span>
            </div>
          }
          @if (lineItemSelections.value_search_box) {
            <div class="preview-item">
              <span class="preview-label value-label-preview">📦 Value Search Box</span>
              <span class="preview-coords">
                ({{lineItemSelections.value_search_box.ulc.x}}, {{lineItemSelections.value_search_box.ulc.y}}) →
                ({{lineItemSelections.value_search_box.lrc.x}}, {{lineItemSelections.value_search_box.lrc.y}})
              </span>
            </div>
          }
        </div>
      }
    </div>

    <!-- Controls -->
    <div class="sidebar-section">
      <h3>⚙️ Display Controls</h3>
      <div class="debug-grid">
        <div class="debug-group">
          <label>Box Opacity:</label>
          <input type="range" [(ngModel)]="boxOpacity" min="0.1" max="1" step="0.1">
          <span>{{boxOpacity | number:'1.1-1'}}</span>
        </div>

        <div class="debug-group">
          <label>Box Display Limit:</label>
          <div class="input-group">
            <input type="number" [(ngModel)]="boxLimit" min="1" max="5000">
          </div>
        </div>
      </div>
    </div>

    <!-- Status -->
    <div class="sidebar-section">
      <h3>🎯 Status</h3>
      <div class="status-display">{{statusMessage}}</div>
    </div>
  </div>
</div>

<!-- Selection Instructions (shown when selection is active) -->
@if (isSelectionActive) {
  <div class="selection-instructions">
    🎯 {{currentSelectionMode.replace('_', ' ') | titlecase}} Selection Mode: Click and drag to draw rectangle around text
  </div>
}

<!-- Loading Spinner Overlay -->
@if (isLoading) {
  <div class="loading-overlay">
    <div class="loading-content">
      <div class="spinner"></div>
      <div class="loading-message">{{loadingMessage}}</div>
    </div>
  </div>
}

